import { injectable } from "inversify";
import DashboardRepository, { DashboardSearchProps, GetOperationReportResponse, Sentiments, SentimentsOnDate } from "../../repositories/dashboard.repository";
import { Either, isRight, right } from "fp-ts/lib/Either";
import ErrorEntity from "../../entities/error.entity";

interface props {
    dashboardRepository: DashboardRepository;
}

@injectable()
class GetOperationReportUseCase {
    private dashboardRepository: DashboardRepository;

    constructor({ dashboardRepository }: props) {
        this.dashboardRepository = dashboardRepository;
    }

    public call = async (_: DashboardSearchProps, onGetOne: (data: GetOperationReportResponse) => void): Promise<Either<ErrorEntity, GetOperationReportResponse>> => {
        let data: GetOperationReportResponse = {};
        const _callCallDetails = async () => {
            const response = await this.dashboardRepository.getCallAverages(_);
            if (isRight(response)) {
                data.averages = response.right;
                data.totalCalls = response.right.totalCalls;
                onGetOne(data);
            }
        }
        const _callTables = async () => {
            const response = await this.dashboardRepository.getTopAndBottomAgents(_);
            if (isRight(response)) {
                data.bestClients = response.right.best;
                data.worstClients = response.right.worst;
                onGetOne(data);
            }
        }

        const _callReasons = async () => {
            const response = await this.dashboardRepository.getReasonForCalls(_);
            if (isRight(response)) {
                data.reasonForCalls = response.right;
                onGetOne(data);
            }
        }

        const _callTarget = async () => {
            const response = await this.dashboardRepository.getScoreDetails(_);
            if (isRight(response)) {
                data.target = response.right;
                onGetOne(data);
            }
        }

        const _callSentiments = async () => {
            const response = await this.dashboardRepository.getSentiments(_);
            if (isRight(response)) {
                data.dayToDay = response.right;
                const sumDates = response.right.reduce((acc: Sentiments, val: SentimentsOnDate) => {
                    return {
                        positive: acc.positive + val.sentiments.positive,
                        negative: acc.negative + val.sentiments.negative,
                        neutral: acc.neutral + val.sentiments.neutral,
                    }
                }, {
                    positive: 0,
                    negative: 0,
                    neutral: 0
                });
                console.log("sumDates", sumDates);
                data.totalSentiments = sumDates;
                onGetOne(data);
            }
        }

        const _callGetAvgScoragePerWeek = async () => {
            const response = await this.dashboardRepository.getAvgScorePerWeek(_);
            if (isRight(response)) {
                data.avgScorePerWeek = response.right;
                onGetOne(data);
            }
        }
        const _callGetAvgScoragePerQuestion = async () => {
            const response = await this.dashboardRepository.getAvgScorePerQuestion(_);
            if (isRight(response)) {
                data.avgScorePerQuestion = response.right;
                onGetOne(data);
            }
        }
        const _callGetAvgScoragePerCategory = async () => {
            const response = await this.dashboardRepository.getAvgScorePerCategory(_);
            if (isRight(response)) {
                data.avgScorePerCategory = response.right;
                onGetOne(data);
            }
        }
        const _callGetAvgScoragePerTypeCall = async () => {
            const response = await this.dashboardRepository.getAvgScorePerTypeCall(_);
            if (isRight(response)) {
                data.avgScorePerTypeCall = response.right;
                onGetOne(data);
            }
        }


        await Promise.all([
            _callCallDetails(),
            _callTables(),
            _callReasons(),
            _callTarget(),
            _callSentiments(),
            _callGetAvgScoragePerWeek(),
            _callGetAvgScoragePerQuestion(),
            _callGetAvgScoragePerCategory(),
            _callGetAvgScoragePerTypeCall(),
        ]);
        return right(data);
    }
}

export default GetOperationReportUseCase;

export const GetOperationReportUseCaseName = "GetOperationReportUseCase";